import React, { useState, useEffect, useCallback } from 'react';
import { Users as UsersIcon, Plus, Pencil, Trash2, Shield, User, Home } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';
import { getUsers, createUser, updateUser, deleteUser, getRooms } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const EMPTY_FORM = { username: '', password: '', role: 'operator', allowed_rooms: [] };

const UserFormDialog = ({ trigger, initial, rooms, onSave }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const handleOpen = (v) => {
    if (v) setForm(initial
      ? { username: initial.username, password: '', role: initial.role, allowed_rooms: initial.allowed_rooms || [] }
      : { ...EMPTY_FORM });
    setOpen(v);
  };

  const toggleRoom = (id) => setForm(f => ({
    ...f,
    allowed_rooms: f.allowed_rooms.includes(id)
      ? f.allowed_rooms.filter(r => r !== id)
      : [...f.allowed_rooms, id]
  }));

  const handleSave = async () => {
    if (!form.username.trim()) { toast.error('Kullanıcı adı giriniz'); return; }
    if (!initial && !form.password) { toast.error('Şifre giriniz'); return; }
    setSaving(true);
    try {
      const payload = { ...form, username: form.username.trim() };
      if (initial && !payload.password) delete payload.password;
      await onSave(payload);
      setOpen(false);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent data-testid="user-form-dialog">
        <DialogHeader>
          <DialogTitle>{initial ? 'Kullanıcıyı Düzenle' : 'Yeni Kullanıcı'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 max-h-[65vh] overflow-y-auto pr-1">
          <div className="space-y-1.5">
            <Label>Kullanıcı Adı *</Label>
            <Input value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
              placeholder="operatör1" disabled={!!initial} data-testid="user-username-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{initial ? 'Yeni Şifre (boş = değiştirme)' : 'Şifre *'}</Label>
            <Input type="password" value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              placeholder={initial ? 'Boş bırakın' : 'En az 4 karakter'} data-testid="user-password-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Rol</Label>
            <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Yönetici (Admin)</SelectItem>
                <SelectItem value="operator">Operatör</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.role === 'operator' && (
            <div className="space-y-2">
              <Label>Erişim İzni Verilen Odalar</Label>
              <p className="text-xs text-muted-foreground">Boş = hiçbir odaya erişim</p>
              {rooms.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">Henüz oda yok</p>
              ) : (
                <div className="space-y-2 max-h-40 overflow-y-auto border border-border rounded-lg p-2">
                  {rooms.map(r => (
                    <label key={r.id} className="flex items-center gap-2 cursor-pointer p-1 hover:bg-muted rounded">
                      <Switch
                        checked={form.allowed_rooms.includes(r.id)}
                        onCheckedChange={() => toggleRoom(r.id)}
                        className="scale-75"
                      />
                      <span className="text-sm">{r.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-user-btn">
            {saving ? 'Kaydediliyor...' : initial ? 'Kaydet' : 'Ekle'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Users = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [u, r] = await Promise.all([getUsers(), getRooms()]);
      setUsers(u.data); setRooms(r.data);
    } catch { toast.error('Veriler yüklenemedi'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (data) => {
    try {
      const res = await createUser(data);
      setUsers(prev => [...prev, res.data]);
      toast.success('Kullanıcı eklendi');
    } catch (e) { toast.error(e.response?.data?.detail || 'Kullanıcı eklenemedi'); throw e; }
  };

  const handleUpdate = async (id, data) => {
    try {
      const res = await updateUser(id, data);
      setUsers(prev => prev.map(u => u.id === id ? res.data : u));
      toast.success('Kullanıcı güncellendi');
    } catch (e) { toast.error(e.response?.data?.detail || 'Güncellenemedi'); throw e; }
  };

  const handleDelete = async (id) => {
    try {
      await deleteUser(id);
      setUsers(prev => prev.filter(u => u.id !== id));
      toast.success('Kullanıcı silindi');
    } catch (e) { toast.error(e.response?.data?.detail || 'Silinemedi'); }
  };

  const roomNameMap = Object.fromEntries(rooms.map(r => [r.id, r.name]));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{fontFamily:'Outfit,sans-serif'}}>Kullanıcılar</h1>
          <p className="text-muted-foreground text-sm mt-1">Kullanıcı hesapları ve erişim yönetimi</p>
        </div>
        <UserFormDialog
          trigger={<Button className="gap-2" data-testid="add-user-btn"><Plus size={16} /> Kullanıcı Ekle</Button>}
          rooms={rooms}
          onSave={handleCreate}
        />
      </div>

      {loading ? (
        <div className="space-y-3">{Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
      ) : users.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-xl">
          <UsersIcon size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-muted-foreground">Henüz kullanıcı yok</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="users-list">
          {users.map(u => (
            <div key={u.id} className="bg-card border border-border rounded-xl p-4 flex items-center gap-4 hover:border-primary/30 transition-all" data-testid={`user-item-${u.id}`}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${u.role === 'admin' ? 'bg-primary/10' : 'bg-muted'}`}>
                {u.role === 'admin' ? <Shield size={18} className="text-primary" /> : <User size={18} className="text-muted-foreground" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold">{u.username}</p>
                  {u.username === me?.username && <Badge variant="outline" className="text-xs">Siz</Badge>}
                  <Badge className={`text-xs ${u.role === 'admin' ? 'bg-primary/10 text-primary border-primary/30' : 'bg-muted text-muted-foreground'}`}>
                    {u.role === 'admin' ? 'Admin' : 'Operatör'}
                  </Badge>
                </div>
                {u.role === 'operator' && (
                  <div className="flex items-center gap-1 mt-1 flex-wrap">
                    {u.allowed_rooms?.length === 0
                      ? <span className="text-xs text-muted-foreground">Oda erişimi yok</span>
                      : u.allowed_rooms?.slice(0, 4).map(rid => (
                        <Badge key={rid} variant="outline" className="text-xs gap-1">
                          <Home size={9} />{roomNameMap[rid] || rid.slice(0, 8)}
                        </Badge>
                      ))
                    }
                    {(u.allowed_rooms?.length || 0) > 4 && (
                      <Badge variant="outline" className="text-xs">+{u.allowed_rooms.length - 4}</Badge>
                    )}
                  </div>
                )}
              </div>
              <div className="flex gap-1 shrink-0">
                <UserFormDialog
                  trigger={<Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-user-${u.id}`}><Pencil size={14} /></Button>}
                  initial={u} rooms={rooms}
                  onSave={(data) => handleUpdate(u.id, data)}
                />
                {u.username !== me?.username && (
                  <ConfirmDeleteDialog
                    title="Kullanıcıyı Sil"
                    description={`"${u.username}" kullanıcısını silmek istediğinizden emin misiniz?`}
                    onConfirm={() => handleDelete(u.id)}
                    trigger={<Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" data-testid={`delete-user-${u.id}`}><Trash2 size={14} /></Button>}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Users;
